import { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import { FaArrowLeft } from "react-icons/fa";
import { useSettings } from "@/redux/settings";
import ResizableBoardFrame from "@/components/board/ResizableBoardFrame";

export const lessonWorkspaceClassName =
  "mx-auto flex w-full flex-col items-center justify-center gap-4 lg:w-fit lg:flex-row lg:items-start";
export const lessonPanelClassName =
  "flex w-full min-w-0 flex-col rounded-lg bg-chess-ui-panel lg:w-[400px] lg:shrink-0 lg:min-h-[var(--lesson-panel-height)]";
export const lessonPrimaryClassName =
  "h-auto min-h-12 w-full whitespace-normal px-6 text-base font-semibold";
export const lessonHintClassName =
  lessonPrimaryClassName + " bg-chess-ui-control text-slate-700 hover:bg-white";
export function lessonPanelStyle(size: number) {
  return { "--lesson-panel-height": `${size + 44}px` } as CSSProperties;
}

export function useLessonBoardSize() {
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  return {
    size:
      settings.isDesktop === false ? Math.max(160, boardSize - 32) : boardSize,
  };
}

export function LessonBoardFrame({
  size,
  children,
}: {
  size: number;
  children: ReactNode;
}) {
  const settings = useSettings();
  return (
    <ResizableBoardFrame
      className="w-fit max-w-full shrink-0"
      size={size}
      enabled={settings.isDesktop !== false}
    >
      {children}
    </ResizableBoardFrame>
  );
}

export function LessonPanelFooter({
  progress,
  primary,
  children,
}: {
  progress: ReactNode;
  primary: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      className="mt-auto shrink-0 rounded-b-lg border-t border-athens-gray-200 bg-athens-gray-200 p-3 md:p-4 [&_[role=progressbar]]:bg-white/60"
      data-lesson-controls
    >
      <div className="mb-3">{progress}</div>
      {primary}
      {children && (
        <div className="mt-2 flex min-h-10 items-center justify-between gap-2">
          {children}
        </div>
      )}
    </div>
  );
}

export function LessonPanelHeader({
  title,
  backHref,
  backLabel,
  detail,
  trailing,
}: {
  title: string;
  backHref: string;
  backLabel: string;
  detail?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <header className="flex min-h-[52px] shrink-0 items-center gap-2 rounded-t-lg bg-chess-ui-header px-4 py-2 text-white">
      <Link
        href={backHref}
        aria-label={backLabel}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-altblue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <FaArrowLeft aria-hidden="true" />
      </Link>
      <h1 className="min-w-0 flex-1 text-sm font-semibold leading-5">
        {title}
      </h1>
      {detail && <div className="sr-only">{detail}</div>}
      {trailing}
    </header>
  );
}
